<template>
  <!--
    O QUADRO DE MISSÕES DO DIA, em papel (D13/D15). A versão antiga era o modal azul-marinho de
    aplicativo: gradiente, título brilhando, duas colunas duplicadas (desktop e mobile inteiros no
    template) e estrelas de caractere. Esta é UMA coluna de papel que serve aos dois tamanhos: as
    missões como linhas do quadro do Cais, a recompensa como nota de rodapé, e o motivo do bloqueio
    escrito no próprio botão (D11).
  -->
  <FishModal :aberto="true" :titulo="$t('dailyMissions.title')" @fechar="$emit('close')">
    <p v-if="isLoading" class="missoes__carregando">{{ $t('dailyMissions.loading') }}</p>

    <template v-else>
      <!-- A mesma linha da tábua do Cais: os dois lugares mostram a missão do mesmo jeito. -->
      <ul class="missoes__lista">
        <LinhaDeMissao v-for="missao in missionsWithProgress" :key="missao.id" :missao="missao" />
      </ul>

      <!-- A recompensa: o prêmio e o tier dele. O mesmo componente da tábua do Cais. -->
      <div class="missoes__recompensa">
        <PremioDasMissoes v-if="rewardFormatted" :premio="rewardFormatted" />
        <FishMeter
          :valor="progressData.completedMissions"
          :maximo="progressData.totalMissions || 1"
          cor="ouro"
          :rotulo="`${$t('dailyMissions.overallProgress')}: ${progressData.completedMissions}/${progressData.totalMissions}`"
          :rotulo-acessivel="$t('dailyMissions.overallProgress')"
        />
      </div>

      <p class="missoes__nota t-legenda">{{ $t('dailyMissions.resetNote') }}</p>
    </template>

    <template #acoes>
      <FishButton
        variante="primario"
        bloco
        :desabilitado="!allMissionsComplete || hasClaimedReward"
        :motivo="motivoDoResgate"
        som="recompensa"
        @click="handleClaimReward"
      >
        {{ hasClaimedReward ? $t('dailyMissions.rewardClaimed') : $t('dailyMissions.claimReward') }}
      </FishButton>
    </template>
  </FishModal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { FishModal, FishButton, FishMeter } from '@/shared/ui';
import LinhaDeMissao from '@/shared/components/LinhaDeMissao.vue';
import PremioDasMissoes from '@/shared/components/PremioDasMissoes.vue';

export default {
  name: 'DailyMissionsModal',
  components: { FishModal, FishButton, FishMeter, LinhaDeMissao, PremioDasMissoes },
  emits: ['close'],
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    ...mapState('dailyMissions', ['missions', 'reward', 'progress']),
    ...mapState('player', ['claimedDailyMissionReward']),
    ...mapGetters('dailyMissions', [
      'missionsWithProgress',
      'allMissionsComplete',
      'rewardClaimed',
      'rewardFormatted'
    ]),
    /**
     * As missões mandam: são a linha que o servidor confere. O perfil só entra
     * como reforço — quem só lia ele reacendia o botão depois de qualquer
     * recarga do jogador, e o servidor recusava o segundo resgate.
     */
    hasClaimedReward() {
      return this.rewardClaimed || this.claimedDailyMissionReward;
    },
    progressData() {
      return this.progress || { completedMissions: 0, totalMissions: 0 };
    },
    /** O que trava o botão, escrito nele (D11): falta missão, ou o prêmio já foi. */
    motivoDoResgate() {
      if (this.hasClaimedReward) return '';
      if (!this.allMissionsComplete) return this.$t('dailyMissions.motivoIncompleto');
      return '';
    }
  },
  methods: {
    async fetchMissionsData() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('dailyMissions/fetchMissions');
      } catch (error) {
        console.error('Error fetching missions:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async handleClaimReward() {
      if (!this.allMissionsComplete || this.hasClaimedReward) return;

      try {
        const result = await this.$store.dispatch('dailyMissions/claimReward');
        if (result && result.success) {
          this.$store.commit('player/SET_CLAIMED_DAILY_MISSION_REWARD', true);
          await this.fetchMissionsData();
          await this.$store.dispatch('SoundController/playSound', {
            category: 'ui',
            type: 'reward'
          });
        } else {
          // O servidor recusou (já resgatado, ou missão que não fechou): relê o
          // quadro para o botão mostrar o estado real, em vez de ficar aceso.
          console.error('Failed to claim reward:', result);
          await this.fetchMissionsData();
        }
      } catch (error) {
        console.error('Error claiming reward:', error);
        await this.fetchMissionsData();
      }
    }
  },
  created() {
    this.fetchMissionsData();
  }
};
</script>

<style scoped>
.missoes__carregando {
  margin: 0;
  color: var(--tinta-fraca);
  font-style: italic;
}

.missoes__lista {
  list-style: none;
  margin: 0 0 var(--e-3);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

.missoes__recompensa {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-3);
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.missoes__nota {
  margin: var(--e-3) 0 0;
  color: var(--tinta-fraca);
}
</style>
